// Global Instructions Rule Applied!
//
// Phase 2 — Email Campaigns compose / draft full-form.
//
// Mirrors distribution_lists' Full.tsx: same shape (cold-load detail,
// AntD form with local state, save/cancel, hard-refresh smoke is the
// normal path). Layout top-to-bottom per the plan:
//   Sender (Send-as picker)   →   Template Select
//   Subject Input             →   Body (Jodit) | Preview (ShadowRichHtml)
//   Attachments (FileUploadManager — only after first save)
//
// Send-as picker semantics (Decisions §6):
//   * Default = operator (the is_self entry from eligible-senders).
//   * Changing the picker pulls a freshly-resolved signature from the
//     backend and offers to swap it in. If the body is "dirty" relative
//     to the most-recently-resolved signature we ask first (confirm-if-
//     dirty); if not, swap silently.
//
// Body sanitization happens server-side in save_email_campaign — the
// frontend hands over the raw Jodit output. The preview pane shows
// whatever the editor currently has (sanitization is not idempotent in a
// way that would mislead the preview, but the user sees their actual
// HTML, not a re-rendered sanitized version).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  Form,
  Input,
  Select,
  Card,
  Row,
  Col,
  Spin,
  Typography,
  Space,
  Button,
  Modal,
  Alert,
  Tag,
  Tooltip,
  Badge,
  Radio,
  Checkbox
} from 'antd'
import {
  ArrowLeftOutlined,
  SendOutlined,
  ReloadOutlined,
  StopOutlined,
  CopyOutlined,
  PaperClipOutlined,
  TagsOutlined,
  ExperimentOutlined,
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  FileSearchOutlined,
  RedoOutlined
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import { dismissErrorToast, showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { store } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { addFormTab, removeTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import {
  TABLE_FILE_URLS_EMAIL_CAMPAIGNS,
  FIELD_EMAIL_CAMPAIGN_ID
} from '../../../../state_manager/globals.ts'
import RichTextEditor from '../../../../core/components/RichTextEditor.jsx'
import type { CKEditorRichTextHandle } from '../../../../core/components/CKEditorRichText.tsx'
import { useEditorEngine } from '../../../global_configs/hooks/useEditorConfig.ts'
import ImportHtmlModal from '../ImportHtmlModal.tsx'
import DispatchStatusBanner, {
  afterCheckAction,
  EMPTY_DISPATCH_PROGRESS,
  hasUnconfirmedRecipients,
  UNCONFIRMED_CHECK,
  isDispatchStalled,
  type DispatchProgress
} from './DispatchStatusBanner.tsx'
import { FileUploadManager } from '../../../../core/components/FileUploadManager/index.tsx'
import PreviewPane, { type PreviewSelection } from '../PreviewPane.tsx'
import type {
  EmailCampaignGridModel,
  EmailSenderOption
} from '../../../../state_manager/data_models/email_campaign_grid_model.ts'
import type {
  EmailCampaignSavePayload,
  EmailChannel,
  EmailChannelValue
} from '../../../../state_manager/interfaces/email.ts'
import type { EmailChannels } from '../../../../state_manager/data_models/email_channels_model.ts'
import {
  fetchEmailCampaignDetail,
  fetchEligibleSenders,
  createEmailCampaign,
  updateEmailCampaign,
  resolveSenderSignature,
  queueEmailCampaign,
  cancelEmailCampaign,
  haltEmailCampaign,
  resumeEmailCampaign,
  reconcileEmailCampaign,
  requeueFailedEmailRecipients,
  retryEmailDispatch,
  fetchEmailChannels,
  fetchEmailTestRecipients,
  testSendEmailCampaign,
  applyTemplateAttachments,
  type EmailTestAccount,
  type EmailTestContact,
  type EmailTestSendRecipientPayload
} from '../../services/emailService.ts'
import {
  fetchEmailDesignTemplatesFiltered,
  fetchEmailDesignTemplateDetail
} from '../../services/emailDesignTemplateService.ts'
import type { EmailDesignTemplateModel } from '../../../../state_manager/data_models/email_design_template_model.ts'
import RecipientsTab from '../RecipientsTab.tsx'
import CcBccEditor from '../CcBccEditor.tsx'
import { useEmailCampaignCopies } from '../../hooks/useEmailCampaignCopies.ts'
import { useEmailCampaignAccountFiles } from '../../hooks/useEmailCampaignAccountFiles.ts'
import ReadOnlyFileList from '../../../../core/components/ReadOnlyFileList.tsx'
import { MERGE_FIELDS } from '../../constants/mergeFields.ts'
import { refreshNoticeCampaign } from '../../../capital_calls/services/capitalCallNoticesService.ts'
import { refreshStatementCampaign } from '../../../investor_statements/services/investorStatementsService.ts'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_CAPITAL_CALLS, TABLE_INVESTOR_STATEMENTS } from '../../../../state_manager/globals.ts'
import { buildChannelRadio, CHANNEL_MAILBOX, mailboxQueueBlock } from '../../utils/emailChannels.ts'
import { lookupOptionsHelp } from '../../../funds/utils/lookupOptionsHelp.tsx'

const { Title, Text } = Typography

// Lazy-loaded so the CKEditor React wrapper is code-split off the main /
// non-email bundles (plan §7.4); the editor build itself loads from the CDN.
const CKEditorRichText = React.lazy(() => import('../../../../core/components/CKEditorRichText.tsx'))

// Append a resolved signature to an email body, with spacing. No-op when there
// is no signature. Robust append-only: the signature lives inside the Jodit
// body, which re-serializes HTML on every edit, so reliable programmatic
// removal isn't safe — removing a signature is a manual edit. Loading a
// template / switching senders re-appends rather than tries to strip.
const SIGNATURE_SEPARATOR_HTML = '<p><br></p>'
function appendSignatureHtml(body: string, signature: string): string {
  const sig = (signature || '').trim()
  if (!sig) return body || ''
  const base = body || ''
  return base.trim() ? `${base}${SIGNATURE_SEPARATOR_HTML}${sig}` : sig
}

interface FullEmailCampaignFormProps {
  recordId?: string | null
  isNew?: boolean
}

interface CampaignFormValues {
  name: string
  sent_as_user_id: string
  email_template_id: string | undefined
  subject: string
  body_html: string
  // Phase 4b: send_mode toggles between Individual (one email per contact)
  // and Team (one email per account; To: + Cc: per account). When 'team',
  // team_greeting_mode controls how the {{first_name}} merge field renders
  // (accounts.team_name vs. comma-joined first names of 'to' contacts).
  send_mode: 'individual' | 'team'
  // How the campaign is sent, independent of send_mode. 'broadcast' (default)
  // and 'transactional' are Postmark streams (with / without an Unsubscribe
  // footer); 'mailbox' sends from the sent-as user's own Outlook mailbox. The
  // options and their wording come from GET /email/channels. A loaded value
  // this build does not know is kept as it is.
  channel: EmailChannelValue
  team_greeting_mode: 'team_name' | 'first_names_list'
  // PR #57 — Stripo HTML import. 'jodit' (default) = body composed in
  // the Jodit editor; sanitized + thin-shell-wrapped on dispatch.
  // 'ckeditor' (Phase 2) = body composed in CKEditor; inline-styled at
  // save (plan §6.1) then sanitized + thin-shell-wrapped on dispatch,
  // exactly like 'jodit'. 'imported' = body is a complete HTML document
  // from an external builder; sanitize + shell bypassed end-to-end.
  // 'imported' drives the body-card render branch (editor vs read-only
  // viewer) + the Send-as picker's signature-resolve gate (architect
  // decision §10.4(i): suppress signature-resolve entirely for imported
  // bodies). 'jodit' vs 'ckeditor' is chosen by the cfg editor engine at
  // save, not by this field.
  body_source: 'jodit' | 'ckeditor' | 'imported'
}

const EMAIL_PATH = '/email'

const SEND_MODE_DEFAULT: 'individual' | 'team' = 'individual'
const CHANNEL_DEFAULT: EmailChannel = 'broadcast'
const TEAM_GREETING_DEFAULT: 'team_name' | 'first_names_list' = 'team_name'

// Toast key for a failed status refresh (the 5 s poll re-raises it at most once).
const STATUS_REFRESH_ERROR_TOAST = 'email-campaign-status-refresh'

// Dispatch progress + halt fields of the campaign detail → banner state.
const toDispatchProgress = (d: EmailCampaignGridModel): DispatchProgress => ({
  secondsSinceProgress: d.email_campaigns__seconds_since_progress ?? null,
  pendingCount: d.email_campaigns__pending_count ?? 0,
  unconfirmedCount: d.email_campaigns__unconfirmed_count ?? 0,
  haltedAt: d.email_campaigns__halted_at ?? null,
  haltedByName: d.email_campaigns__halted_by_name ?? null,
  stallWarningSeconds: d.email_campaigns__stall_warning_seconds ?? null
})

// Merge fields moved to ../../constants/mergeFields.ts so the template
// editor reuses the same list. Edit there.

export default function FullEmailCampaignForm({
  recordId: propRecordId,
  isNew: propIsNew
}: FullEmailCampaignFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [form] = Form.useForm<CampaignFormValues>()
  // Compose-body editor engine — cfg-driven toggle
  // (globalConfigs.editor_config.engine). 'jodit' (default) keeps the existing
  // editor; 'ckeditor' swaps in the CKEditor 5 cloud editor behind the same
  // prop contract. Phase 2: a 'ckeditor' save writes body_source='ckeditor'
  // and runs inline-at-save (plan §6.1).
  const editorEngine = useEditorEngine()

  // Imperative ref to the CKEditor wrapper (engine='ckeditor' only). handleSave
  // calls getInlineHtml() for inline-at-save; null for the Jodit engine or
  // before the editor mounts.
  const editorRef = useRef<CKEditorRichTextHandle | null>(null)

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNewInitial = propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId

  const formTabKey = `email-form-${isNewInitial ? 'new' : 'edit'}-${recordId || 'new'}`

  // Email-templates catalog used to come from cfg.email_templates (the
  // React shell dropdown). 2026-05-29 — the Template dropdown was
  // repurposed to load operator-defined design templates from
  // email_design_templates instead; the cfg.email_templates concept
  // stays internal (saved campaigns keep their email_template_id; new
  // campaigns leave it NULL so the dispatch defaults to GeneralUpdate).

  // AntD v5 with React 19: static Modal.confirm / Modal.warning don't always
  // portal correctly (no theme/context, sometimes silently no-op). Modal.useModal
  // gives us a context-aware modal API + a ContextHolder we render in the tree
  // — works in every supported AntD/React combination.
  const [modalApi, modalContextHolder] = Modal.useModal()

  const [eligibleSenders, setEligibleSenders] = useState<EmailSenderOption[]>([])
  // Send type options (cfg) and the state of the mailbox channel. null until
  // loaded, and when the read failed: the campaign's own send type is then
  // shown read-only.
  const [emailChannels, setEmailChannels] = useState<EmailChannels | null>(null)
  const [loading, setLoading] = useState(!isNewInitial)
  const [saving, setSaving] = useState(false)
  const [queuing, setQueuing] = useState(false)
  const [notFound, setNotFound] = useState(false)
  // After first save in 'new' mode the form transitions to 'edit' mode and
  // gains a real id; the attachments section unlocks at that point.
  const [persistedId, setPersistedId] = useState<string | null>(
    isNewInitial ? null : (recordId === 'new' ? null : recordId)
  )
  const isEditing = !!persistedId

  // Phase 4 status tracking — driven by the campaign detail fetch + a
  // 5s interval refetch while the campaign is in flight. Terminal states
  // ('sent', 'partially_failed', 'failed') stop the poll.
  // Phase 4b adds campaignTeamCount so the queue modal can read
  // "N recipients across M teams" in team mode.
  const [campaignStatus, setCampaignStatus] = useState<string | null>(null)
  const [campaignRecipientCount, setCampaignRecipientCount] = useState<number>(0)
  // The record this campaign was created to send the documents of: the
  // capital call of a notice email (capital call notices, stage 7) or the
  // statement run of a statement email (investor statements, A3.14 stage
  // 6); null for any other campaign. Such an email keeps Send type on the
  // mailbox channel and takes its recipients and per-account attachments
  // from that record ("Refresh from ...") instead of the picker.
  const [sourceLink, setSourceLink] = useState<{
    kind: 'capital_call' | 'statement_run'
    id: string
    name: string
    // a statement email: the document groups it carries ('Fund reports',
    // 'Statements'; docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1) and,
    // for a part of a set, which one ('Fund reports (email 2 of 3)'; d.3)
    detail?: string | null
  } | null>(null)
  const { openFormInTab } = useTabManagerContext()
  const [campaignTeamCount, setCampaignTeamCount] = useState<number>(0)
  const [campaignSentCount, setCampaignSentCount] = useState<number>(0)
  const [campaignFailedCount, setCampaignFailedCount] = useState<number>(0)
  const [campaignDispatchedAt, setCampaignDispatchedAt] = useState<string | null>(null)
  const [campaignCompletedAt, setCampaignCompletedAt] = useState<string | null>(null)
  // Advisory queue-warning threshold (cfg-driven via the detail RPC; null →
  // use a frontend default). When the message count is at/above this and the
  // campaign has Cc/Bcc copies, the queue-confirm modal warns about copy
  // multiplication.
  const [ccBccThreshold, setCcBccThreshold] = useState<number | null>(null)
  // Dispatch progress + halt state (stall warning, Halt / Resume; 2026-09-15).
  const [dispatchProgress, setDispatchProgress] = useState<DispatchProgress>(EMPTY_DISPATCH_PROGRESS)

  const isInFlight = campaignStatus === 'queued' || campaignStatus === 'sending'
  const isHalted = campaignStatus === 'halted'
  const isTerminal =
    campaignStatus === 'sent' ||
    campaignStatus === 'partially_failed' ||
    campaignStatus === 'failed' ||
    campaignStatus === 'cancelled'
  const isLockedForEditing = isInFlight || isHalted || isTerminal
  const isStalled = isDispatchStalled(campaignStatus, dispatchProgress)
  // The campaign's channel and sender as the form holds them (live while a
  // draft is edited, the loaded values once locked). Mirrored into state
  // where the form is filled and in onValuesChange - not Form.useWatch,
  // which under React 19 raises "state update on a component that hasn't
  // mounted yet" on this form.
  const [watchedChannel, setWatchedChannel] = useState<EmailChannelValue>(CHANNEL_DEFAULT)
  const [watchedSenderId, setWatchedSenderId] = useState<string | undefined>(undefined)
  const isMailboxChannel = watchedChannel === CHANNEL_MAILBOX
  // Unconfirmed recipients are checked against what actually left: Postmark's
  // message history, or the sender's own mailbox for a mailbox campaign.
  const canCheckUnconfirmed = hasUnconfirmedRecipients(campaignStatus, dispatchProgress)
  const unconfirmedCheck = isMailboxChannel ? UNCONFIRMED_CHECK.mailbox : UNCONFIRMED_CHECK.postmark
  // "Re-queue failed": a mailbox campaign's failed recipients never left the
  // mailbox, so they can go back in the queue.
  const canRequeueFailed =
    isMailboxChannel && (campaignStatus === 'failed' || campaignStatus === 'partially_failed')

  // Campaign-level Cc / Bcc copy recipients (rendered in the Compose card).
  // Persists immediately (draft-only); the editor reads from cc/bcc. Inert
  // until the draft is saved (persistedId set).
  const {
    cc: ccRecipients,
    bcc: bccRecipients,
    addCopy,
    removeCopy,
    searchContacts: searchCopyContacts
  } = useEmailCampaignCopies({ campaignId: persistedId })

  // Per-account attachments (capital call notices, stage 5): files that
  // belong to one account each. Read-only here; the Recipients pane shows
  // them per account and refetches when the staged recipients change.
  const accountFiles = useEmailCampaignAccountFiles({ campaignId: persistedId })
  const accountFileCount = useMemo(
    () => (accountFiles.accountFiles?.data ?? []).reduce((n, row) => n + row.prog__file_count, 0),
    [accountFiles.accountFiles]
  )
  const accountsWithFiles = accountFiles.summary?.accounts_with_files ?? 0

  // The signature we last resolved server-side. Used to detect "the user
  // has typed since the signature was injected" before offering to swap.
  const [lastResolvedSignature, setLastResolvedSignature] = useState<string>('')

  // Phase 4b — which merge-field chip is currently hovered (drives the
  // copy-icon's opacity so the affordance only shows on hover).
  const [hoveredMergeField, setHoveredMergeField] = useState<string | null>(null)

  // Phase 4b — right-pane preview selection. Cleared whenever the
  // campaign changes (different draft) or the user toggles send_mode
  // (the row shape changes between modes).
  const [previewSelection, setPreviewSelection] = useState<PreviewSelection>(null)
  useEffect(() => {
    setPreviewSelection(null)
  }, [persistedId])

  // Phase 4b — Body-card icon modals: Merge fields chooser + Attachments
  // manager. Attachments list comes from the detail RPC's
  // email_campaigns__attachments array; we re-fetch on attachments-modal
  // close so the inline chip strip + badge count update after upload /
  // delete operations the modal performs.
  const [mergeFieldsOpen, setMergeFieldsOpen] = useState(false)
  const [attachmentsOpen, setAttachmentsOpen] = useState(false)
  // PR #57 — Stripo HTML import modal. Triggered by the Body card's
  // "Import HTML" button (compose mode) or "Re-import" button
  // (already-imported draft). Submit writes body_html + flips the
  // form's body_source to 'imported'; the next save call persists.
  const [importHtmlOpen, setImportHtmlOpen] = useState(false)
  // 2026-05-29 — Template dropdown now lists email_design_templates (the
  // operator-defined design library); the legacy cfg.email_templates
  // React shell concept stays internal — campaigns save with
  // email_template_id=NULL so the dispatch defaults to GeneralUpdate.
  const [availableTemplates, setAvailableTemplates] = useState<EmailDesignTemplateModel[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [applyingTemplateId, setApplyingTemplateId] = useState<string | null>(null)
  // Toolbar "Insert signature" in-flight (resolving the sender's signature).
  const [insertingSignature, setInsertingSignature] = useState(false)
  const [selectedDesignTemplateId, setSelectedDesignTemplateId] = useState<string | undefined>(
    undefined
  )
  // Template applied before the draft's first save: there is no campaign
  // row yet for the template's attachments to copy onto, so we stash the
  // template id and run the copy right after the create succeeds.
  const [pendingTemplateAttachmentsId, setPendingTemplateAttachmentsId] = useState<string | null>(
    null
  )
  const [campaignAttachments, setCampaignAttachments] = useState<
    Array<{ id: string; file_name: string; file_url: string; date_created: string }>
  >([])

  // Templates dropdown options come from email_design_templates (the
  // operator-defined library). Sorted by recency (most-recently-modified
  // first) since operators tend to reuse what they just authored.
  // Track D1 — Test Send modal state. The cfg row points at a "test
  // account" (typically Acequia Capital); the modal shows that account's
  // contacts as checkboxes (operator de/selects) plus an ad-hoc free-
  // text input for addresses not wired to the test account. Contact-
  // linked sends use real merge_data; ad-hoc sends get visible
  // placeholder synthesis in the edge fn. testSendCheckedContactIds is
  // the Set<contact_id> of currently-selected curated contacts.
  // Defaults to the is_self contact pre-checked so a single click sends
  // to the operator's mailbox.
  const [testSendOpen, setTestSendOpen] = useState(false)
  const [testSendSending, setTestSendSending] = useState(false)
  const [testSendAccount, setTestSendAccount] = useState<EmailTestAccount | null>(null)
  const [testSendContacts, setTestSendContacts] = useState<EmailTestContact[]>([])
  const [testSendCheckedContactIds, setTestSendCheckedContactIds] = useState<Set<string>>(new Set())
  const [testSendAdHocText, setTestSendAdHocText] = useState('')
  // A campaign with per-account attachments is tested AS one staged account:
  // the test carries that account's merge values and its own files.
  const [testSendAsAccountId, setTestSendAsAccountId] = useState<string | undefined>(undefined)
  // Per-recipient errors from the last test-send attempt — rendered as
  // a persistent alert in the modal so the operator can see WHICH
  // address failed and the provider's reason (e.g. "Inactive address"
  // for a Postmark-suppressed mailbox after an earlier unsubscribe).
  const [testSendLastErrors, setTestSendLastErrors] = useState<
    Array<{ email: string; message: string }>
  >([])

  const templateOptions = useMemo(
    () =>
      [...availableTemplates]
        .sort((a, b) =>
          (b.email_design_templates__date_modified ?? '').localeCompare(
            a.email_design_templates__date_modified ?? ''
          )
        )
        .map((t) => ({
          value: t.email_design_templates__id,
          label: t.email_design_templates__name
        })),
    [availableTemplates]
  )
  const senderOptions = useMemo(
    () => eligibleSenders.map((s) => ({ value: s.value, label: s.label })),
    [eligibleSenders]
  )

  // Send type radio: the cfg channels; one this environment cannot send on is
  // disabled with its reason; a send type it does not list stays as loaded,
  // read-only.
  const channelRadio = useMemo(
    () => buildChannelRadio(emailChannels, watchedChannel || CHANNEL_DEFAULT),
    [emailChannels, watchedChannel]
  )
  const channelHelp = useMemo(
    () =>
      lookupOptionsHelp(
        'How the campaign is sent.',
        (emailChannels?.channels ?? []).map((c) => ({ label: c.label, description: c.description }))
      ),
    [emailChannels]
  )
  // Why a mailbox campaign cannot be queued as it stands (the channel is off
  // here, or the chosen sender's mailbox is not enabled) - shown in the
  // Sender card and on the disabled Queue button.
  const mailboxBlock = useMemo(
    () =>
      mailboxQueueBlock(
        emailChannels,
        watchedChannel,
        eligibleSenders.find((s) => s.value === watchedSenderId)?.email
      ),
    [emailChannels, watchedChannel, watchedSenderId, eligibleSenders]
  )

  // ─── Mount-time fetches ────────────────────────────────────────────────

  // Track D1 — load the cfg-pointed test account + its contacts once on
  // mount. Independent of the campaign so the modal can open as soon as
  // the form is saved (no recipients required). Pre-checks the is_self
  // contact (whose email matches the caller's system_users.email) so a
  // single click sends to the operator's own mailbox; other contacts
  // default to unchecked.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchEmailTestRecipients(token)
      if (cancelled) return
      if (res.error) {
        // Don't toast — the modal is still usable with ad-hoc addresses
        // only. Surface the error in the modal instead.
        console.warn('Failed to load test recipients cfg:', res.error)
      }
      const account = res.data?.account || null
      const contacts = res.data?.contacts || []
      setTestSendAccount(account)
      setTestSendContacts(contacts)
      setTestSendCheckedContactIds(
        new Set(contacts.filter((c) => c.is_self).map((c) => c.contact_id))
      )
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchEmailChannels(token)
      if (cancelled) return
      if (res.error) showErrorToast(`Couldn't load the send types: ${res.error}`)
      setEmailChannels(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchEligibleSenders(token)
      if (cancelled) return
      if (res.error) showErrorToast(res.error)
      setEligibleSenders(res.data || [])

      // For a NEW draft, default Send-as = the operator (the is_self row)
      // and resolve their signature into the body upfront.
      if (isNewInitial) {
        const me = (res.data || []).find((s) => s.is_self) || (res.data || [])[0]
        if (me) {
          form.setFieldsValue({ sent_as_user_id: me.value })
          setWatchedSenderId(me.value)
          const sigRes = await resolveSenderSignature({ sent_as_user_id: me.value }, token)
          if (cancelled) return
          if (sigRes.data?.html) {
            form.setFieldsValue({ body_html: sigRes.data.html })
            setLastResolvedSignature(sigRes.data.html)
          }
        }
        dispatch(updateTabTitle({ key: formTabKey, title: 'New Email' }))
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isNewInitial) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchEmailCampaignDetail(String(recordId), token)
      if (cancelled) return
      if (res.error) {
        showErrorToast(res.error)
        setLoading(false)
        return
      }
      const d: EmailCampaignGridModel | null = res.data
      if (!d) {
        setNotFound(true)
        setLoading(false)
        return
      }
      const sendMode = (d.email_campaigns__send_mode === 'team' ? 'team' : 'individual') as 'individual' | 'team'
      // Kept as loaded - a send type this build does not know must not be
      // turned into Broadcast and saved back.
      const channel: EmailChannelValue = d.email_campaigns__channel || CHANNEL_DEFAULT
      const greetMode = (d.email_campaigns__team_greeting_mode === 'first_names_list'
        ? 'first_names_list'
        : 'team_name') as 'team_name' | 'first_names_list'
      form.setFieldsValue({
        name: d.email_campaigns__name,
        sent_as_user_id: d.email_campaigns__sent_as_user_id,
        email_template_id: d.email_campaigns__email_template_id ?? undefined,
        subject: d.email_campaigns__subject,
        body_html: d.email_campaigns__body_html ?? '',
        send_mode: sendMode,
        channel,
        team_greeting_mode: greetMode,
        // Preserve the persisted body_source. 'imported' = verbatim HTML
        // document; 'ckeditor' = CKEditor-composed (inline-styled at save);
        // older rows / anything else fall back to 'jodit' (the DB default).
        body_source: (d.email_campaigns__body_source === 'imported'
          ? 'imported'
          : d.email_campaigns__body_source === 'ckeditor'
          ? 'ckeditor'
          : 'jodit')
      })
      setWatchedChannel(channel)
      setWatchedSenderId(d.email_campaigns__sent_as_user_id)
      setPersistedId(d.email_campaigns__id)
      setCampaignStatus(d.email_campaigns__status ?? null)
      setSourceLink(
        d.email_campaigns__capital_call_id
          ? { kind: 'capital_call', id: d.email_campaigns__capital_call_id, name: d.capital_calls__name || 'Capital call' }
          : d.email_campaigns__investor_statement_run_id
            ? {
                kind: 'statement_run',
                id: d.email_campaigns__investor_statement_run_id,
                name: d.investor_statement_runs__name || 'Statement run',
                detail:
                  [
                    d.prog__statement_document_groups_label,
                    (d.investor_statement_email_campaigns__part_count ?? 1) > 1
                      ? `(email ${d.investor_statement_email_campaigns__part_no ?? 1} of ${d.investor_statement_email_campaigns__part_count})`
                      : null
                  ]
                    .filter(Boolean)
                    .join(' ') || null
              }
            : null
      )
      setCampaignAttachments(d.email_campaigns__attachments ?? [])
      setCampaignRecipientCount(d.email_campaigns__recipient_count ?? 0)
      setCampaignTeamCount(d.email_campaigns__team_count ?? 0)
      setCcBccThreshold(d.email_campaigns__cc_bcc_large_list_threshold ?? null)
      setCampaignSentCount(d.email_campaigns__sent_count ?? 0)
      setCampaignFailedCount(d.email_campaigns__failed_count ?? 0)
      setCampaignDispatchedAt(d.email_campaigns__dispatched_at ?? null)
      setCampaignCompletedAt(d.email_campaigns__completed_at ?? null)
      setDispatchProgress(toDispatchProgress(d))
      dispatch(
        updateTabTitle({
          key: formTabKey,
          title: `Edit ${d.email_campaigns__name || 'Email'}`
        })
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [recordId, isNewInitial, form, dispatch, formTabKey])

  // ─── Body / preview wiring ─────────────────────────────────────────────

  const handleBodyChange = useCallback(
    (next: string) => {
      form.setFieldsValue({ body_html: next })
    },
    [form]
  )

  // ─── Design templates: lazy-fetch on mount + apply on pick ─────────────
  //
  // 2026-05-29 — the Template dropdown now lists email_design_templates.
  // Pick → fetch detail (the list RPC omits body_html to keep the grid
  // payload thin) → confirm-if-dirty → copy body_html + subject_default
  // into the form.

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoadingTemplates(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await fetchEmailDesignTemplatesFiltered(
          [{ key: 'email_design_templates__date_modified', direction: 'desc' }],
          [],
          1,
          100,
          token
        )
        if (cancelled) return
        if (!res.error) {
          setAvailableTemplates(res.data)
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Resolve the current Send-as user's signature HTML. Returns '' when no
  // sender is set, the sender has no signature, or the body is an imported
  // document (which owns its own signature).
  const resolveCurrentSenderSignature = useCallback(async (): Promise<string> => {
    const sentAsUserId = form.getFieldValue('sent_as_user_id')
    if (!sentAsUserId) return ''
    if (form.getFieldValue('body_source') === 'imported') return ''
    const token = getAccessToken(store.getState().auth)
    const res = await resolveSenderSignature({ sent_as_user_id: sentAsUserId }, token)
    return res.data?.html || ''
  }, [form])

  // Refetch the campaign detail and refresh every status-derived state
  // slice. Declared before applyDesignTemplate (which awaits it) and used
  // by the Phase 4 in-flight polling effect further down.
  // campaignIdOverride: right after the first save the persistedId state
  // update hasn't landed in this closure yet — the create path passes the
  // fresh id explicitly (template-attachment copy refresh).
  const refreshCampaignStatus = useCallback(async (campaignIdOverride?: string) => {
    const campaignId = campaignIdOverride ?? persistedId
    if (!campaignId) return
    const token = getAccessToken(store.getState().auth)
    const res = await fetchEmailCampaignDetail(campaignId, token)
    if (res.error || !res.data) {
      // Keyed so the 5 s poll doesn't stack toasts; dismissed on the next
      // successful refresh. A silent return here hid a stuck status strip.
      showErrorToast(`Couldn't refresh the campaign status: ${res.error || 'campaign not found'}`, {
        key: STATUS_REFRESH_ERROR_TOAST
      })
      return
    }
    dismissErrorToast(STATUS_REFRESH_ERROR_TOAST)
    const d = res.data
    setCampaignStatus(d.email_campaigns__status ?? null)
    setCampaignRecipientCount(d.email_campaigns__recipient_count ?? 0)
    setCampaignTeamCount(d.email_campaigns__team_count ?? 0)
    setCampaignSentCount(d.email_campaigns__sent_count ?? 0)
    setCampaignFailedCount(d.email_campaigns__failed_count ?? 0)
    setCampaignDispatchedAt(d.email_campaigns__dispatched_at ?? null)
    setCampaignCompletedAt(d.email_campaigns__completed_at ?? null)
    setCampaignAttachments(d.email_campaigns__attachments ?? [])
    setDispatchProgress(toDispatchProgress(d))
  }, [persistedId])

  // "Refresh from capital call" / "Refresh from statement run" (Recipients
  // tab): the recipients and the per-account attachments are rebuilt from
  // the record as it is now. The tab re-reads the staged rows when this
  // resolves.
  const handleRefreshFromSource = useCallback(async () => {
    if (!persistedId || !sourceLink) return
    const token = getAccessToken(store.getState().auth)
    if (sourceLink.kind === 'statement_run') {
      const res = await refreshStatementCampaign(sourceLink.id, persistedId, token)
      if (!res.success) {
        showErrorToast(res.error || 'Failed to refresh from the statement run')
        return
      }
      // a part of a set: every draft of the set was refreshed with it
      toast.success(
        (res.partCount ?? 1) > 1
          ? `Recipients and statements refreshed from the statement run for ${res.refreshed ?? 1} of the set's ${res.partCount} emails${
              res.skipped?.length ? ` (left as sent: ${res.skipped.join(', ')})` : ''
            }`
          : 'Recipients and statements refreshed from the statement run'
      )
    } else {
      const res = await refreshNoticeCampaign(sourceLink.id, persistedId, token)
      if (!res.success) {
        showErrorToast(res.error || 'Failed to refresh from the capital call')
        return
      }
      toast.success('Recipients and notices refreshed from the capital call')
    }
    await refreshCampaignStatus()
  }, [persistedId, sourceLink, refreshCampaignStatus])

  const applyDesignTemplate = useCallback(
    async (templateId: string) => {
      setApplyingTemplateId(templateId)
      try {
        const token = getAccessToken(store.getState().auth)
        const detail = await fetchEmailDesignTemplateDetail(templateId, token)
        if (detail.error || !detail.data) {
          showErrorToast(detail.error || 'Failed to load template body')
          return
        }
        const next = detail.data
        const nextBody = next.email_design_templates__body_html ?? ''
        // Re-append the current sender's signature so loading a template does
        // not discard it (a template carries only the body). No-op for imported
        // bodies or when there is no signature.
        const sig = await resolveCurrentSenderSignature()
        const bodyWithSig = appendSignatureHtml(nextBody, sig)
        // Pre-fill subject only when empty — never clobber an edited subject.
        const currentSubject = (form.getFieldValue('subject') || '').trim()
        const nextSubject =
          currentSubject || next.email_design_templates__subject_default || ''
        form.setFieldsValue({ body_html: bodyWithSig, subject: nextSubject })
        if (sig) setLastResolvedSignature(sig)
        setSelectedDesignTemplateId(templateId)
        toast.success(`Loaded "${next.email_design_templates__name}"`)

        // Bring the template's attachments along. Saved draft → copy now
        // (backend dedups by file_name, so re-applying is a no-op). Unsaved
        // → stash the template id; handleSave copies right after create.
        const templateAttachments = next.email_design_templates__attachments ?? []
        if (templateAttachments.length === 0) {
          setPendingTemplateAttachmentsId(null)
          return
        }
        if (!persistedId) {
          setPendingTemplateAttachmentsId(templateId)
          toast.info(
            `${templateAttachments.length} template attachment(s) will be added when the draft is saved`
          )
          return
        }
        setPendingTemplateAttachmentsId(null)
        const copyRes = await applyTemplateAttachments(persistedId, templateId, token)
        if (copyRes.error || !copyRes.data) {
          showErrorToast(copyRes.error || 'Failed to load template attachments')
          return
        }
        if (copyRes.data.copied_count > 0) {
          toast.success(`${copyRes.data.copied_count} template attachment(s) added`)
        }
        await refreshCampaignStatus()
      } finally {
        setApplyingTemplateId(null)
      }
    },
    [form, resolveCurrentSenderSignature, persistedId, refreshCampaignStatus]
  )

  // Toolbar "Insert signature": append the current sender's signature to the
  // body. The way to (re)add it after loading a template or switching senders.
  const insertSignature = useCallback(async () => {
    setInsertingSignature(true)
    try {
      const sig = await resolveCurrentSenderSignature()
      if (!sig) {
        showErrorToast('No signature to insert for the selected sender.')
        return
      }
      const current = form.getFieldValue('body_html') || ''
      form.setFieldsValue({ body_html: appendSignatureHtml(current, sig) })
      setLastResolvedSignature(sig)
    } finally {
      setInsertingSignature(false)
    }
  }, [form, resolveCurrentSenderSignature])

  const handleTemplateChange = useCallback(
    (templateId: string | undefined) => {
      // Clearing the picker — just unselect; body stays as-is. Any not-yet-
      // copied attachment stash goes with it (the template is no longer
      // what the draft is based on).
      if (!templateId) {
        setSelectedDesignTemplateId(undefined)
        setPendingTemplateAttachmentsId(null)
        return
      }
      const currentBody = (form.getFieldValue('body_html') || '').trim()
      const looksDirty =
        currentBody.length > 0 && currentBody !== lastResolvedSignature
      if (!looksDirty) {
        applyDesignTemplate(templateId)
        return
      }
      const t = availableTemplates.find(
        (x) => x.email_design_templates__id === templateId
      )
      const name = t?.email_design_templates__name ?? 'this template'
      modalApi.confirm({
        title: 'Replace the current body?',
        content: (
          <span>
            Loading <Text code>{name}</Text> will replace your draft body.
            Continue?
          </span>
        ),
        okText: 'Replace body',
        okButtonProps: { danger: true },
        cancelText: 'Keep my body',
        onOk: () => applyDesignTemplate(templateId)
      })
    },
    [applyDesignTemplate, availableTemplates, form, lastResolvedSignature, modalApi]
  )

  // Phase 4b: when the user toggles Send mode, offer to swap the body's
  // {{first_name}} placeholder for the right team-mode equivalent (or back
  // again on toggle to Individual). The dispatch no longer rewrites
  // {{first_name}} in team mode — it renders empty there — so the swap is
  // important UX. We only act when the body actually contains the source
  // placeholder; otherwise the toggle is silent.
  const swapMergePlaceholder = useCallback(
    (from: RegExp, to: string) => {
      const current = form.getFieldValue('body_html') || ''
      if (!from.test(current)) return
      const next = current.replace(from, to)
      form.setFieldsValue({ body_html: next })
    },
    [form]
  )

  const handleFormValuesChange = useCallback(
    (changed: Partial<CampaignFormValues>) => {
      if (changed.channel !== undefined) setWatchedChannel(changed.channel)
      if (changed.sent_as_user_id !== undefined) setWatchedSenderId(changed.sent_as_user_id)
      if (changed.send_mode === undefined) return
      // Clear the right-pane selection — the row shape differs between
      // modes (per-contact rows in individual; per-team rows in team).
      setPreviewSelection(null)
      const next = changed.send_mode
      const body = (form.getFieldValue('body_html') || '') as string

      if (next === 'team') {
        // Default swap target is {{team_name}}; if the user prefers
        // {{first_names_list}} they can edit by hand or pick that chip
        // from the merge-field strip.
        if (/\{\{\s*first_name\s*\}\}/.test(body)) {
          modalApi.confirm({
            title: 'Use team greeting?',
            content: (
              <span>
                Team mode renders {`{{first_name}}`} as empty (no single contact). Swap
                every {`{{first_name}}`} in the body for <Text code>{'{{team_name}}'}</Text>?
                (Use <Text code>{'{{first_names_list}}'}</Text> instead by editing the
                body or picking it from the merge-field strip.)
              </span>
            ),
            okText: 'Swap to {{team_name}}',
            cancelText: 'Keep as is',
            onOk: () => swapMergePlaceholder(/\{\{\s*first_name\s*\}\}/g, '{{team_name}}')
          })
        }
      } else if (next === 'individual') {
        // On toggle back to individual, offer to swap {{team_name}} or
        // {{first_names_list}} (whichever appears) back to {{first_name}}.
        // We only prompt for one swap — if both appear (uncommon), the
        // user can edit by hand.
        const hasTeamName = /\{\{\s*team_name\s*\}\}/.test(body)
        const hasFirstNames = /\{\{\s*first_names_list\s*\}\}/.test(body)
        if (hasTeamName || hasFirstNames) {
          const from = hasTeamName ? /\{\{\s*team_name\s*\}\}/g : /\{\{\s*first_names_list\s*\}\}/g
          const fromLabel = hasTeamName ? '{{team_name}}' : '{{first_names_list}}'
          modalApi.confirm({
            title: 'Use contact first name?',
            content: (
              <span>
                Individual mode renders one email per contact. Swap every{' '}
                <Text code>{fromLabel}</Text> in the body for <Text code>{`{{first_name}}`}</Text>?
              </span>
            ),
            okText: 'Swap to {{first_name}}',
            cancelText: 'Keep as is',
            onOk: () => swapMergePlaceholder(from, '{{first_name}}')
          })
        }
      }
    },
    [form, modalApi, swapMergePlaceholder]
  )

  // ─── Start-from-template (2026-05-29) ─────────────────────────────────
  // Send-as picker change → re-resolve the chosen user's signature. If the body
  // is still empty or just the previous signature (untouched new draft), swap
  // it for the new one; otherwise the body is kept untouched (metadata-only) and
  // the operator re-adds the new signature via "Insert signature". We never
  // overwrite real body content on a sender change.
  const handleSenderChange = useCallback(
    async (sentAsUserId: string) => {
      form.setFieldsValue({ sent_as_user_id: sentAsUserId })
      setWatchedSenderId(sentAsUserId)
      // PR #57, architect decision §10.4(i): for imported bodies, the
      // signature lives inside the operator-authored HTML document and
      // the dispatch ships that document verbatim. Re-resolving and
      // injecting a Reservoir-side signature would destroy the import,
      // so the picker change here is metadata-only: update sent_as_user_id
      // and skip the signature swap entirely.
      const bodySource = form.getFieldValue('body_source')
      if (bodySource === 'imported') {
        return
      }
      const token = getAccessToken(store.getState().auth)
      const res = await resolveSenderSignature({ sent_as_user_id: sentAsUserId }, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to resolve signature for that sender')
        return
      }
      const nextSig = res.data.html
      const currentBody = form.getFieldValue('body_html') || ''
      // Only swap the body when it is empty or still just the previously
      // resolved signature (an untouched new draft). When the body holds real
      // content (a loaded template and/or edits) we never overwrite it — the
      // sender change is metadata-only, and the operator can pull in this
      // sender's signature with the "Insert signature" button.
      const bodyIsJustSignature =
        !currentBody.trim() || currentBody === lastResolvedSignature
      setLastResolvedSignature(nextSig)
      if (bodyIsJustSignature) {
        form.setFieldsValue({ body_html: nextSig })
      } else {
        toast.info(
          'Sender updated. Your body was kept — use the “Insert signature” button to add this sender’s signature.'
        )
      }
    },
    [form, lastResolvedSignature]
  )

  // ─── Status refetch + polling (Phase 4) ───────────────────────────────
  //
  // While the campaign is in flight ('queued' or 'sending') we refetch the
  // detail RPC every 5s so the user sees rollups tick up without manual
  // intervention. The poll stops as soon as the campaign reaches a
  // terminal state (no websockets — repo-consistent).
  // (refreshCampaignStatus itself is declared above applyDesignTemplate,
  // which awaits it after copying template attachments.)

  // Halted campaigns keep polling too: the batch on the wire at the moment of
  // the halt settles afterwards, and the waiting count should follow it.
  useEffect(() => {
    if (!persistedId) return
    if (!isInFlight && !isHalted) return
    const interval = setInterval(() => {
      refreshCampaignStatus()
    }, 5000)
    return () => clearInterval(interval)
  }, [persistedId, isInFlight, isHalted, refreshCampaignStatus])

  // A dispatch started on a FAILED / PARTIALLY FAILED campaign (Retry
  // dispatch, Re-queue failed) is fire-and-forget: the campaign only leaves
  // its failed status when the dispatch claims it, moments after the click,
  // and the 5 s poll above does not run for a failed campaign. So look again
  // a few times; once the status is 'sending' the poll takes over.
  const followUpTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  useEffect(
    () => () => {
      followUpTimersRef.current.forEach(clearTimeout)
    },
    []
  )
  const refreshAfterDispatchStart = useCallback(async () => {
    await refreshCampaignStatus()
    followUpTimersRef.current.forEach(clearTimeout)
    followUpTimersRef.current = [1500, 4000, 8000].map((ms) =>
      setTimeout(() => {
        refreshCampaignStatus()
      }, ms)
    )
  }, [refreshCampaignStatus])

  // ─── Queue + retry (Phase 4) ──────────────────────────────────────────

  const handleQueue = useCallback(async () => {
    if (!persistedId) return
    if (campaignRecipientCount === 0) {
      modalApi.warning({
        title: 'No recipients staged',
        content:
          "There aren't any staged recipients on this draft yet. Open the Recipients tab to resolve a distribution list first."
      })
      return
    }
    const senderOption = eligibleSenders.find(
      (s) => s.value === form.getFieldValue('sent_as_user_id')
    )
    const senderLabel = senderOption?.label || '(unknown sender)'
    // The mailbox a mailbox campaign leaves from: the sender's address.
    const senderMailbox = senderOption?.email || senderLabel
    // Phase 4b: in team mode we send ONE message per account, not per
    // recipient. The modal copy reflects that so the user knows how many
    // actual emails will be sent.
    const isTeamMode = form.getFieldValue('send_mode') === 'team'
    const viaMailbox = form.getFieldValue('channel') === CHANNEL_MAILBOX
    const recipientPlural = campaignRecipientCount === 1 ? '' : 's'
    const teamPlural      = campaignTeamCount === 1 ? '' : 's'

    // Cc/Bcc copies ride on every message, so each copied address receives one
    // per message: one per recipient (individual mode) or one per team (team
    // mode). Warn when that count is at/above the cfg threshold so a large
    // send doesn't silently mass-copy a Cc'd/Bcc'd address.
    const copyCount     = ccRecipients.length + bccRecipients.length
    const messageCount  = isTeamMode ? campaignTeamCount : campaignRecipientCount
    const warnThreshold = ccBccThreshold ?? 25
    const showCopyWarning = copyCount > 0 && messageCount >= warnThreshold

    modalApi.confirm({
      title: 'Queue this campaign?',
      content: (
        <div>
          {isTeamMode ? (
            <p>
              <strong>{campaignTeamCount}</strong> team{teamPlural} (
              <strong>{campaignRecipientCount}</strong> recipient{recipientPlural} in total) will
              receive an email from <strong>{senderLabel}</strong>. In team mode each account gets
              one email with To and Cc filled from the staged recipients.
            </p>
          ) : (
            <p>
              <strong>{campaignRecipientCount}</strong> recipient{recipientPlural} will receive an
              email from <strong>{senderLabel}</strong>.
            </p>
          )}
          {copyCount > 0 && (
            <p style={{ marginTop: 8 }}>
              <strong>{ccRecipients.length}</strong> Cc and{' '}
              <strong>{bccRecipients.length}</strong> Bcc{' '}
              {copyCount === 1 ? 'address' : 'addresses'} will be copied on{' '}
              {isTeamMode ? 'each team email' : 'every email'}.
              {showCopyWarning && (
                <Text type='warning' style={{ display: 'block', marginTop: 4 }}>
                  Heads up: each Cc/Bcc address will receive {messageCount}{' '}
                  cop{messageCount === 1 ? 'y' : 'ies'} (one per{' '}
                  {isTeamMode ? 'team email' : 'recipient'}).
                </Text>
              )}
            </p>
          )}
          {accountFileCount > 0 && (
            <p style={{ marginTop: 8 }}>Each account receives its own attachment(s).</p>
          )}
          {viaMailbox && (
            <p style={{ marginTop: 8 }}>
              Sent from the mailbox <strong>{senderMailbox}</strong> and saved in its Sent Items. No
              unsubscribe link and no delivery tracking.
            </p>
          )}
          <p style={{ color: '#8c8c8c', marginTop: 8 }}>
            Once queued the draft is locked. The dispatch runs in the background
            and the status banner below updates as it progresses.
          </p>
        </div>
      ),
      okText: 'Queue campaign',
      cancelText: 'Cancel',
      onOk: async () => {
        setQueuing(true)
        try {
          const token = getAccessToken(store.getState().auth)
          const result = await queueEmailCampaign(persistedId, token)
          if (result.error || !result.data) {
            const errMsg = result.error || 'Failed to queue campaign'
            // 403 = delegation; 400 = validation; surface verbatim.
            showErrorToast(errMsg)
            return
          }
          toast.success(
            viaMailbox
              ? `Queued — sending from the mailbox ${senderMailbox} (${result.data.provider_key})`
              : `Queued — dispatch in flight via ${result.data.provider_key}`
          )
          await refreshCampaignStatus()
        } finally {
          setQueuing(false)
        }
      }
    })
  }, [persistedId, campaignRecipientCount, campaignTeamCount, ccRecipients, bccRecipients, ccBccThreshold, eligibleSenders, form, refreshCampaignStatus, modalApi, accountFileCount])

  // ─── Test send (Track D1) ─────────────────────────────────────────────
  //
  // Builds the structured recipient payload from (a) the checked curated
  // contacts on the test account + (b) ad-hoc emails parsed from the
  // free-text input. Dedups by lowercased email — first-seen wins so a
  // contact-linked entry beats a duplicate ad-hoc entry of the same
  // address (keeps merge_data real). Posts to the backend test-send
  // route, which is authoritative for validation.

  const parseAdHocEmails = useCallback((text: string): string[] => {
    const out: string[] = []
    const seen = new Set<string>()
    for (const raw of (text || '').split(/[\s,;]+/)) {
      const e = raw.trim().toLowerCase()
      if (!e || seen.has(e)) continue
      seen.add(e)
      out.push(e)
    }
    return out
  }, [])

  const testSendRecipientsPayload = useMemo<EmailTestSendRecipientPayload[]>(() => {
    const out: EmailTestSendRecipientPayload[] = []
    const seen = new Set<string>()
    // Contact-linked entries first so they win the dedup.
    for (const c of testSendContacts) {
      if (!testSendCheckedContactIds.has(c.contact_id)) continue
      const k = c.email.toLowerCase()
      if (seen.has(k)) continue
      seen.add(k)
      out.push({ email: k, contact_id: c.contact_id, account_id: c.account_id })
    }
    for (const e of parseAdHocEmails(testSendAdHocText)) {
      if (seen.has(e)) continue
      seen.add(e)
      out.push({ email: e })
    }
    return out
  }, [testSendContacts, testSendCheckedContactIds, testSendAdHocText, parseAdHocEmails])

  const openTestSend = useCallback(() => {
    // Re-prime the checked set on open to the is_self default — covers
    // the case where the cfg load finished AFTER an earlier open.
    setTestSendCheckedContactIds(
      new Set(testSendContacts.filter((c) => c.is_self).map((c) => c.contact_id))
    )
    setTestSendAdHocText('')
    setTestSendAsAccountId(undefined)
    setTestSendLastErrors([])
    setTestSendOpen(true)
  }, [testSendContacts])

  // The staged accounts a test can be sent as (only asked for when the
  // campaign carries per-account attachments).
  const testSendAccountOptions = useMemo(
    () =>
      (accountFiles.accountFiles?.data ?? [])
        .filter((row) => row.prog__is_staged)
        .map((row) => ({
          value: row.accounts__id,
          label:
            row.prog__file_count > 0
              ? `${row.accounts__name} (${row.prog__file_count} file${row.prog__file_count === 1 ? '' : 's'})`
              : `${row.accounts__name} (no file)`
        })),
    [accountFiles.accountFiles]
  )
  const testSendNeedsAccount = accountFiles.carriesFiles

  const handleTestSend = useCallback(async () => {
    if (!persistedId) return
    if (testSendRecipientsPayload.length === 0) {
      showErrorToast('Pick at least one recipient.')
      return
    }
    if (testSendNeedsAccount && !testSendAsAccountId) {
      showErrorToast('Choose the account whose attachments the test should carry.')
      return
    }
    setTestSendSending(true)
    setTestSendLastErrors([])
    try {
      const token = getAccessToken(store.getState().auth)
      const result = await testSendEmailCampaign(
        persistedId,
        testSendRecipientsPayload,
        token,
        testSendNeedsAccount ? testSendAsAccountId ?? null : null
      )
      if (result.error || !result.data) {
        // Hard failure (controller / network / non-2xx). Keep the modal
        // open so the operator can adjust and retry.
        showErrorToast(result.error || 'Failed to send test email.')
        return
      }
      const { sent_count, failed_count, errors } = result.data
      if (failed_count === 0) {
        toast.success(
          `Test send to ${sent_count} recipient${sent_count === 1 ? '' : 's'} dispatched.`
        )
        setTestSendOpen(false)
      } else {
        // Partial-or-full per-recipient failure (e.g. Postmark-suppressed
        // address after a prior unsubscribe). Show the errors in an
        // inline alert so the operator can see WHICH addresses failed
        // and WHY, and keep the modal open.
        setTestSendLastErrors(errors || [])
        if (sent_count > 0) {
          toast.warn(
            `Test send finished — ${sent_count} sent, ${failed_count} failed. See details below.`
          )
        } else {
          showErrorToast(
            `Test send failed for ${failed_count} recipient${failed_count === 1 ? '' : 's'}. See details below.`
          )
        }
      }
    } finally {
      setTestSendSending(false)
    }
  }, [persistedId, testSendRecipientsPayload, testSendNeedsAccount, testSendAsAccountId])

  const handleRetryDispatch = useCallback(async () => {
    if (!persistedId) return
    setQueuing(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const result = await retryEmailDispatch(persistedId, token)
      if (result.error) {
        showErrorToast(result.error)
        return
      }
      toast.success('Dispatch re-invoked — refreshing status')
      await refreshAfterDispatchStart()
    } finally {
      setQueuing(false)
    }
  }, [persistedId, refreshAfterDispatchStart])

  // "Re-queue failed" (mailbox campaigns): put the failed recipients back in
  // the queue, then start the dispatch for them. With nothing newly re-queued
  // the dispatch is still tried once - an earlier re-queue whose dispatch
  // never started leaves recipients queued - and only when that too finds
  // nothing is there nothing to do.
  const handleRequeueFailed = useCallback(async () => {
    if (!persistedId) return
    setQueuing(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const requeue = await requeueFailedEmailRecipients(persistedId, token)
      if (requeue.error || !requeue.data) {
        showErrorToast(requeue.error || 'Failed to re-queue the failed recipients')
        return
      }
      const n = requeue.data.requeued
      const retry = await retryEmailDispatch(persistedId, token)
      if (retry.error) {
        if (n === 0) {
          toast.info('Nothing to re-queue')
        } else {
          showErrorToast(
            `Re-queued ${n} recipient${n === 1 ? '' : 's'}, but the dispatch did not start: ${retry.error} Use Re-queue failed again to start it.`
          )
        }
        await refreshCampaignStatus()
        return
      }
      toast.success(
        n > 0
          ? `Re-queued ${n} recipient${n === 1 ? '' : 's'} — sending`
          : 'Dispatch started for the recipients already in the queue'
      )
      await refreshAfterDispatchStart()
    } finally {
      setQueuing(false)
    }
  }, [persistedId, refreshCampaignStatus, refreshAfterDispatchStart])

  // Cancel ends a campaign that is queued (nothing went yet) or halted (the
  // recipients not yet sent are cancelled; what went stays sent).
  const handleCancel = useCallback(async () => {
    if (!persistedId) return
    const halted = campaignStatus === 'halted'
    const count = halted ? dispatchProgress.pendingCount : campaignRecipientCount
    const recipientPlural = count === 1 ? '' : 's'
    modalApi.confirm({
      title: halted ? 'Cancel this halted campaign?' : 'Cancel this queued campaign?',
      content: halted ? (
        <div>
          <p>
            <strong>{count}</strong> recipient{recipientPlural} not yet sent will be marked{' '}
            <strong>cancelled</strong> and will not be sent. Recipients who already received the email stay
            sent.
          </p>
          <p style={{ color: '#8c8c8c', marginTop: 8 }}>
            A cancelled campaign cannot be resumed. While a recipient is still unconfirmed the cancel is
            refused — check the unconfirmed recipients first.
          </p>
        </div>
      ) : (
        <div>
          <p>
            <strong>{count}</strong> queued recipient{recipientPlural} will be
            marked <strong>cancelled</strong> and the campaign will not be dispatched.
          </p>
          <p style={{ color: '#8c8c8c', marginTop: 8 }}>
            If the dispatch has already started (status flipped to <em>sending</em>) the cancel
            will be refused — once a message has been handed over for delivery it can't be unsent
            from here.
          </p>
        </div>
      ),
      okText: 'Cancel campaign',
      okButtonProps: { danger: true },
      cancelText: halted ? 'Keep halted' : 'Keep queued',
      onOk: async () => {
        setQueuing(true)
        try {
          const token = getAccessToken(store.getState().auth)
          const result = await cancelEmailCampaign(persistedId, token)
          if (result.error || !result.data) {
            showErrorToast(result.error || 'Failed to cancel campaign')
            return
          }
          toast.success(
            `Cancelled — ${result.data.cancelled_recipients} recipient${result.data.cancelled_recipients === 1 ? '' : 's'} marked cancelled`
          )
          await refreshCampaignStatus()
        } finally {
          setQueuing(false)
        }
      }
    })
  }, [
    persistedId,
    campaignStatus,
    campaignRecipientCount,
    dispatchProgress.pendingCount,
    refreshCampaignStatus,
    modalApi
  ])

  const handleHalt = useCallback(async () => {
    if (!persistedId) return
    modalApi.confirm({
      title: 'Halt this campaign?',
      content: (
        <div>
          <p>
            Sending stops after the message{isMailboxChannel ? '' : 's'} currently being handed
            over for delivery. The{' '}
            <strong>{dispatchProgress.pendingCount}</strong> recipient
            {dispatchProgress.pendingCount === 1 ? '' : 's'} not yet sent will wait until you resume.
          </p>
          <p style={{ color: '#8c8c8c', marginTop: 8 }}>
            Emails already handed over for delivery can't be recalled.
          </p>
        </div>
      ),
      okText: 'Halt sending',
      okButtonProps: { danger: true },
      cancelText: 'Keep sending',
      onOk: async () => {
        setQueuing(true)
        try {
          const token = getAccessToken(store.getState().auth)
          const result = await haltEmailCampaign(persistedId, token)
          if (result.error || !result.data) {
            showErrorToast(result.error || 'Failed to halt campaign')
            return
          }
          toast.success(
            isMailboxChannel
              ? 'Halted — sending stops after the current message'
              : 'Halted — sending stops after the current batch'
          )
          await refreshCampaignStatus()
        } finally {
          setQueuing(false)
        }
      }
    })
  }, [persistedId, dispatchProgress.pendingCount, refreshCampaignStatus, modalApi, isMailboxChannel])

  const handleResume = useCallback(async () => {
    if (!persistedId) return
    modalApi.confirm({
      title: 'Resume sending?',
      content: (
        <p>
          The campaign continues with the <strong>{dispatchProgress.pendingCount}</strong> recipient
          {dispatchProgress.pendingCount === 1 ? '' : 's'} not yet sent. Recipients who already received
          it are not sent again.
        </p>
      ),
      okText: 'Resume sending',
      cancelText: 'Stay halted',
      onOk: async () => {
        setQueuing(true)
        try {
          const token = getAccessToken(store.getState().auth)
          const result = await resumeEmailCampaign(persistedId, token)
          if (result.error || !result.data) {
            showErrorToast(result.error || 'Failed to resume campaign')
            return
          }
          toast.success(
            result.data.resumed
              ? `Resumed — sending to ${result.data.queued_count} remaining recipient${result.data.queued_count === 1 ? '' : 's'}`
              : 'Nothing left to send — campaign finalized'
          )
          await refreshCampaignStatus()
        } finally {
          setQueuing(false)
        }
      }
    })
  }, [persistedId, dispatchProgress.pendingCount, refreshCampaignStatus, modalApi])

  const handleReconcile = useCallback(async () => {
    if (!persistedId) return
    const n = dispatchProgress.unconfirmedCount
    modalApi.confirm({
      title: unconfirmedCheck.title,
      content: (
        <div>
          <p>
            <strong>{n}</strong> recipient{n === 1 ? ' is' : 's are'} unconfirmed. Each is looked up in{' '}
            {unconfirmedCheck.lookup}:
          </p>
          <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
            <li>{unconfirmedCheck.found} → marked <strong>sent</strong></li>
            <li>not found → put back in the <strong>queue</strong></li>
          </ul>
          <p style={{ color: '#8c8c8c', marginTop: 8 }}>
            Nothing is sent by this check. Re-queued recipients are sent when you{' '}
            {afterCheckAction(campaignStatus, isMailboxChannel)}.
          </p>
        </div>
      ),
      okText: unconfirmedCheck.okText,
      cancelText: 'Cancel',
      onOk: async () => {
        setQueuing(true)
        try {
          const token = getAccessToken(store.getState().auth)
          const result = await reconcileEmailCampaign(persistedId, token)
          if (result.error || !result.data) {
            showErrorToast(result.error || unconfirmedCheck.failed)
            return
          }
          const { confirmed_sent: sent, requeued, settling } = result.data
          toast.success(
            `${unconfirmedCheck.done} — ${sent} confirmed sent, ${requeued} re-queued` +
              (settling > 0 ? ` (${settling} still settling — check again shortly)` : '')
          )
          await refreshCampaignStatus()
        } finally {
          setQueuing(false)
        }
      }
    })
  }, [
    persistedId,
    campaignStatus,
    dispatchProgress.unconfirmedCount,
    refreshCampaignStatus,
    modalApi,
    unconfirmedCheck,
    isMailboxChannel
  ])

  // ─── Save / cancel ────────────────────────────────────────────────────

  const closeToList = useCallback(() => {
    navigate(EMAIL_PATH)
    dispatch(removeTab({ key: formTabKey }))
  }, [navigate, dispatch, formTabKey])

  const handleSave = useCallback(async () => {
    let values: CampaignFormValues
    try {
      values = await form.validateFields()
    } catch {
      return // AntD shows field errors inline
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)

      // Resolve body_source + body_html before building the payload.
      //   'imported'  → ships verbatim (controller skips sanitize).
      //   'ckeditor'  → tag the row 'ckeditor' AND run inline-at-save
      //                 (plan §6.1): convert the editor's class-based content
      //                 into self-contained inline-styled HTML ONCE, here at
      //                 save. Falls back to the form's class-based body if the
      //                 editor/export is unavailable so a save never blocks.
      //   else        → legacy 'jodit'.
      let resolvedBodySource: 'jodit' | 'ckeditor' | 'imported'
      let resolvedBodyHtml = values.body_html || ''
      if (values.body_source === 'imported') {
        resolvedBodySource = 'imported'
      } else if (editorEngine === 'ckeditor') {
        resolvedBodySource = 'ckeditor'
        try {
          const inline = await editorRef.current?.getInlineHtml()
          if (inline && inline.trim()) resolvedBodyHtml = inline
        } catch {
          /* keep the class-based body_html fallback */
        }
      } else {
        resolvedBodySource = 'jodit'
      }

      const payload: EmailCampaignSavePayload = {
        name: values.name.trim(),
        subject: values.subject || '',
        body_html: resolvedBodyHtml,
        sent_as_user_id: values.sent_as_user_id,
        email_template_id: values.email_template_id || null,
        // Phase 4b: pick up the user's send_mode / team_greeting_mode
        // toggles. Defaults applied here as a belt-and-braces guard in
        // case the form values get cleared (shouldn't happen — both have
        // initialValues + Radio.Groups that always have a selection).
        send_mode: values.send_mode || SEND_MODE_DEFAULT,
        channel: values.channel || CHANNEL_DEFAULT,
        team_greeting_mode: values.team_greeting_mode || TEAM_GREETING_DEFAULT,
        body_source: resolvedBodySource
      }
      const result = isEditing
        ? await updateEmailCampaign(String(persistedId), payload, token)
        : await createEmailCampaign(payload, token)

      if (result.error || !result.data) {
        showErrorToast(result.error || 'Failed to save email campaign')
        return
      }
      toast.success(isEditing ? 'Email campaign updated' : 'Draft created')
      // After first create the form stays open, transitioning to edit
      // mode so the attachments section unlocks. Navigate to the new
      // record's URL with replace:true so the back button doesn't
      // return to the empty /new form, and so a hard-refresh of the
      // URL bar lands on the saved draft instead of starting over.
      const newId = result.data.email_campaigns__id
      setPersistedId(newId)
      // A template with attachments was applied before this first save —
      // there was no campaign row to copy onto then. Copy now that the
      // draft exists, then refresh so the chip strip + badge populate.
      if (!isEditing && newId && pendingTemplateAttachmentsId) {
        const copyRes = await applyTemplateAttachments(
          newId,
          pendingTemplateAttachmentsId,
          token
        )
        setPendingTemplateAttachmentsId(null)
        if (copyRes.error || !copyRes.data) {
          showErrorToast(copyRes.error || 'Failed to load template attachments')
        } else if (copyRes.data.copied_count > 0) {
          toast.success(`${copyRes.data.copied_count} template attachment(s) added`)
        }
        await refreshCampaignStatus(newId)
      }
      if (!isEditing && newId) {
        const cleanName = values.name.trim() || 'Email'
        navigate(`/email/forms/full/${newId}`, { replace: true })
        // The new→edit URL change makes useTabManager spawn a fresh
        // `…-form-edit-<id>` tab; without cleanup the original
        // `…-form-new-new` tab lingers (it shows in the tab bar) and steals
        // focus on close — closeToList's removeTab drops the edit tab and
        // tabSlice then activates the leftover new tab, so the first
        // back-arrow surfaces an empty New-Email form instead of closing.
        // Open the edit tab and drop the stale new tab here.
        // add-before-remove keeps the new-new tab off the active slot when
        // removed → no flash, no leftover tab.
        dispatch(
          addFormTab({
            module: 'email',
            recordId: newId,
            isNew: false,
            title: cleanName
          })
        )
        dispatch(removeTab({ key: formTabKey }))
        dispatch(
          updateTabTitle({
            key: `email-form-edit-${newId}`,
            title: `Edit ${cleanName}`
          })
        )
      } else {
        dispatch(
          updateTabTitle({
            key: formTabKey,
            title: `Edit ${values.name.trim() || 'Email'}`
          })
        )
      }
    } finally {
      setSaving(false)
    }
  }, [form, isEditing, persistedId, dispatch, formTabKey, navigate, editorEngine, pendingTemplateAttachmentsId, refreshCampaignStatus])

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className='h-full overflow-auto bg-white'>
      {modalContextHolder}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={closeToList} />
          <Title level={4} style={{ margin: 0 }}>
            {isEditing ? 'Edit Email Draft' : 'New Email Draft'}
          </Title>
        </Space>
        <Space>
          <Button onClick={closeToList}>Cancel</Button>
          {isEditing && (campaignStatus === 'draft' || campaignStatus === null) && (
            <Tooltip
              title={
                !isEditing
                  ? 'Save the draft first to enable test send'
                  : 'Send a [TEST]-prefixed copy to selected recipients without queueing the campaign'
              }
            >
              <Button
                icon={<ExperimentOutlined />}
                onClick={openTestSend}
                disabled={notFound || saving || testSendSending}
              >
                Test send
              </Button>
            </Tooltip>
          )}
          {isEditing && (campaignStatus === 'draft' || campaignStatus === null) && (
            // A blocked step stays visible, disabled, with the reason
            // (wrapping span so the Tooltip fires on a disabled Button).
            <Tooltip title={mailboxBlock || undefined}>
              <span>
                <Button
                  icon={<SendOutlined />}
                  loading={queuing}
                  onClick={handleQueue}
                  disabled={notFound || saving || campaignRecipientCount === 0 || !!mailboxBlock}
                >
                  Queue campaign
                </Button>
              </span>
            </Tooltip>
          )}
          {isEditing && campaignStatus === 'sending' && (
            <Button
              danger
              icon={<PauseCircleOutlined />}
              loading={queuing}
              onClick={handleHalt}
              disabled={notFound}
            >
              Halt
            </Button>
          )}
          {isEditing && canCheckUnconfirmed && (
            <Button
              icon={<FileSearchOutlined />}
              loading={queuing}
              onClick={handleReconcile}
              disabled={notFound}
            >
              {unconfirmedCheck.button}
            </Button>
          )}
          {isEditing && isHalted && (
            <Button
              icon={<PlayCircleOutlined />}
              loading={queuing}
              onClick={handleResume}
              disabled={notFound}
            >
              Resume
            </Button>
          )}
          {isEditing && (campaignStatus === 'queued' || isHalted) && (
            <Button
              danger
              icon={<StopOutlined />}
              loading={queuing}
              onClick={handleCancel}
              disabled={notFound}
            >
              Cancel campaign
            </Button>
          )}
          {isEditing && canRequeueFailed && (
            <Tooltip title='Put the recipients that failed back in the queue and send to them again. Recipients who already received the email are not sent again.'>
              <Button
                icon={<RedoOutlined />}
                loading={queuing}
                onClick={handleRequeueFailed}
                disabled={notFound}
              >
                Re-queue failed
              </Button>
            </Tooltip>
          )}
          {isEditing &&
            // On a failed mailbox campaign "Re-queue failed" re-queues and
            // starts the dispatch in one step, so Retry is not offered there.
            (((campaignStatus === 'failed' || campaignStatus === 'partially_failed') && !canRequeueFailed) ||
              (campaignStatus === 'queued' && isStalled)) && (
            <Button
              icon={<ReloadOutlined />}
              loading={queuing}
              onClick={handleRetryDispatch}
              disabled={notFound}
            >
              Retry dispatch
            </Button>
          )}
          <Button
            type='primary'
            loading={saving}
            onClick={handleSave}
            disabled={notFound || isLockedForEditing}
          >
            {isEditing ? 'Save' : 'Create draft'}
          </Button>
        </Space>
      </div>

      {isEditing && campaignStatus && campaignStatus !== 'draft' && !notFound && (
        <DispatchStatusBanner
          status={campaignStatus}
          sentCount={campaignSentCount}
          failedCount={campaignFailedCount}
          recipientCount={campaignRecipientCount}
          dispatchedAt={campaignDispatchedAt}
          completedAt={campaignCompletedAt}
          autoRefreshing={isInFlight || isHalted}
          progress={dispatchProgress}
          viaMailbox={isMailboxChannel}
        />
      )}

      {notFound ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
          This email campaign was not found (it may have been deleted).
        </div>
      ) : (
        <Spin spinning={loading}>
          <div style={{ padding: '12px 16px 16px' }}>
            <Form
              form={form}
              layout='vertical'
              autoComplete='off'
              initialValues={{
                body_html: '',
                send_mode: SEND_MODE_DEFAULT,
                channel: CHANNEL_DEFAULT,
                team_greeting_mode: TEAM_GREETING_DEFAULT,
                // PR #57 — default new drafts to 'jodit'. Existing
                // drafts override this in the detail-load effect.
                body_source: 'jodit'
              }}
              onValuesChange={handleFormValuesChange}
            >
              {/* Hidden fields so form.validateFields() picks them up.
                  body_html is driven by the Jodit editor's onChange;
                  team_greeting_mode is kept on the schema (NOT NULL
                  CHECK, default 'team_name') for backward compat but no
                  longer drives the dispatch — the user picks {{team_name}}
                  or {{first_names_list}} explicitly in the body.
                  body_source is set programmatically by the Import
                  HTML modal (PR #57) or by the detail-load effect. */}
              <Form.Item name='body_html' hidden>
                <Input />
              </Form.Item>
              <Form.Item name='team_greeting_mode' hidden>
                <Input />
              </Form.Item>
              <Form.Item name='body_source' hidden>
                <Input />
              </Form.Item>

              {/* Phase 4b layout: 50/50 split. Left = compose form;
                  right = recipients list on top + preview pane below.
                  Each column manages its own internal scroll so the
                  sticky header + status banner stay visible. */}
              <Row gutter={16} style={{ minHeight: 'calc(100vh - 180px)' }}>
                {/* ── LEFT: compose ────────────────────────────────────── */}
                <Col
                  xs={24}
                  md={12}
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Card
                    size='small'
                    title='Sender'
                    style={{ marginBottom: 12, flex: '0 0 auto' }}
                  >
                    {sourceLink && (
                      <div style={{ marginBottom: 8 }}>
                        <Text type='secondary'>
                          {sourceLink.kind === 'statement_run' ? 'Statement run: ' : 'Capital call: '}
                        </Text>
                        <button
                          type='button'
                          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                          onClick={() =>
                            openFormInTab(
                              sourceLink.kind === 'statement_run' ? TABLE_INVESTOR_STATEMENTS : TABLE_CAPITAL_CALLS,
                              sourceLink.id,
                              sourceLink.name
                            )
                          }
                        >
                          {sourceLink.name}
                        </button>
                        {sourceLink.detail ? <Text type='secondary'> · {sourceLink.detail}</Text> : null}
                      </div>
                    )}
                    <Row gutter={12}>
                      <Col xs={24} md={7}>
                        <Form.Item
                          name='sent_as_user_id'
                          label='Send as'
                          rules={[{ required: true, message: 'Required' }]}
                          tooltip="Recipients see this person as From. Defaults to you; anyone who's delegated to you appears here too."
                          style={{ marginBottom: 8 }}
                        >
                          <Select
                            showSearch
                            allowClear={false}
                            placeholder='Pick a sender'
                            options={senderOptions}
                            optionFilterProp='label'
                            onChange={handleSenderChange}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={5}>
                        <Form.Item
                          name='send_mode'
                          label='Send mode'
                          tooltip='Individual sends one email per contact. Team sends one email per account (To + Cc per account).'
                          style={{ marginBottom: 8 }}
                        >
                          <Radio.Group
                            disabled={isLockedForEditing}
                            optionType='button'
                            buttonStyle='solid'
                            size='small'
                          >
                            <Radio.Button value='individual'>Individual</Radio.Button>
                            <Radio.Button value='team'>Team</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name='channel'
                          label='Send type'
                          tooltip={channelHelp}
                          style={{ marginBottom: 8 }}
                        >
                          <Radio.Group optionType='button' buttonStyle='solid' size='small'>
                            {channelRadio.options.map((o) => (
                              <Tooltip
                                key={o.value}
                                title={
                                  sourceLink && o.value !== CHANNEL_MAILBOX
                                    ? sourceLink.kind === 'statement_run'
                                      ? 'A statement email carries each investor\'s own statements, which only a mailbox send can attach.'
                                      : 'A notice email carries each investor\'s own notice, which only a mailbox send can attach.'
                                    : o.disabled
                                    ? o.tooltip
                                    : undefined
                                }
                              >
                                {/* disabled per button: a Radio's own
                                    `disabled` wins over the group's, so the
                                    lock is OR-ed in here. A notice email
                                    stays on the mailbox channel. */}
                                <Radio.Button
                                  value={o.value}
                                  disabled={
                                    o.disabled ||
                                    isLockedForEditing ||
                                    channelRadio.disabled ||
                                    (!!sourceLink && o.value !== CHANNEL_MAILBOX)
                                  }
                                >
                                  {o.label}
                                </Radio.Button>
                              </Tooltip>
                            ))}
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                    {/* Why this draft cannot be queued on the mailbox channel
                        as it stands (the same sentence is the disabled Queue
                        button's tooltip). */}
                    {mailboxBlock && !isLockedForEditing && (
                      <Alert type='warning' showIcon message={mailboxBlock} style={{ marginBottom: 8 }} />
                    )}
                    <Row gutter={12}>
                      {/* Template sits on this row since the mailbox channel
                          (2026-10-01): three Send type buttons need the width
                          it used to share on the row above. */}
                      <Col xs={24} md={6}>
                        <Form.Item
                          label='Template'
                          tooltip='Pick a saved design template. Loads the body + pre-fills the subject when empty.'
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            showSearch
                            allowClear
                            placeholder={loadingTemplates ? 'Loading…' : '(none)'}
                            options={templateOptions}
                            optionFilterProp='label'
                            // The column is narrow; the popup opens wider so
                            // template names read in full (owner 2026-09-16).
                            popupMatchSelectWidth={380}
                            value={selectedDesignTemplateId}
                            loading={loadingTemplates || applyingTemplateId !== null}
                            onChange={handleTemplateChange}
                            disabled={isLockedForEditing}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={7}>
                        <Form.Item
                          name='name'
                          label='Internal name'
                          rules={[
                            { required: true, message: 'Required' },
                            { whitespace: true, message: 'Required' }
                          ]}
                          tooltip='Operator-only label for this draft. Not seen by recipients.'
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder='e.g. Q2 LP update' maxLength={200} allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={11}>
                        <Form.Item
                          name='subject'
                          label='Subject'
                          tooltip='Sent verbatim. Use the merge-field chips below to insert placeholders like {{team_name}}.'
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            placeholder='e.g. Hi {{team_name}}, our Q2 update'
                            maxLength={500}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={12} style={{ marginTop: 12 }}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label='Cc'
                          tooltip='Copied on every email this campaign sends. Pick a contact or type an email and press Enter.'
                          style={{ marginBottom: 0 }}
                        >
                          {persistedId ? (
                            <CcBccEditor
                              kind='cc'
                              items={ccRecipients}
                              disabled={isLockedForEditing}
                              onAdd={addCopy}
                              onRemove={removeCopy}
                              onSearchContacts={searchCopyContacts}
                            />
                          ) : (
                            <Text type='secondary' style={{ fontSize: 12 }}>
                              Save the draft to add Cc recipients.
                            </Text>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label='Bcc'
                          tooltip='Hidden from other recipients; copied on every email. Pick a contact or type an email and press Enter.'
                          style={{ marginBottom: 0 }}
                        >
                          {persistedId ? (
                            <CcBccEditor
                              kind='bcc'
                              items={bccRecipients}
                              disabled={isLockedForEditing}
                              onAdd={addCopy}
                              onRemove={removeCopy}
                              onSearchContacts={searchCopyContacts}
                            />
                          ) : (
                            <Text type='secondary' style={{ fontSize: 12 }}>
                              Save the draft to add Bcc recipients.
                            </Text>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  <Card
                    title='Body'
                    size='small'
                    style={{
                      marginBottom: 12,
                      flex: '1 1 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 320
                    }}
                    bodyStyle={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column' }}
                    extra={
                      <Space size={4}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, curr) => prev.body_source !== curr.body_source}
                        >
                          {() => {
                            const isImported = form.getFieldValue('body_source') === 'imported'
                            return (
                              <Tooltip
                                title={
                                  isImported
                                    ? 'Re-import an HTML document to replace the current body'
                                    : 'Paste or upload a complete HTML document from Stripo or another builder'
                                }
                              >
                                <Button
                                  size='small'
                                  onClick={() => setImportHtmlOpen(true)}
                                  disabled={isLockedForEditing}
                                >
                                  {isImported ? 'Re-import' : 'Import HTML'}
                                </Button>
                              </Tooltip>
                            )
                          }}
                        </Form.Item>
                        <Tooltip title='Insert a merge field placeholder'>
                          <Button
                            size='small'
                            icon={<TagsOutlined />}
                            onClick={() => setMergeFieldsOpen(true)}
                          >
                            Merge fields
                          </Button>
                        </Tooltip>
                        <Tooltip title="Append the selected sender's signature to the body">
                          <Button
                            size='small'
                            icon={<EditOutlined />}
                            loading={insertingSignature}
                            onClick={insertSignature}
                            disabled={isLockedForEditing}
                          >
                            Insert signature
                          </Button>
                        </Tooltip>
                        <Tooltip
                          title={
                            isEditing
                              ? 'Manage attachments (shared across all recipients)'
                              : 'Save the draft first to add attachments'
                          }
                        >
                          {/* Wrapping span so Tooltip still fires when the
                              underlying Button is disabled (Antd quirk). */}
                          <span>
                            <Badge count={campaignAttachments.length} size='small' offset={[-4, 4]}>
                              <Button
                                size='small'
                                icon={<PaperClipOutlined />}
                                onClick={() => setAttachmentsOpen(true)}
                                disabled={!isEditing}
                              >
                                Attachments
                              </Button>
                            </Badge>
                          </span>
                        </Tooltip>
                      </Space>
                    }
                  >
                    {/*
                      PR #57 — body editor branches on body_source.
                        'jodit'    → Jodit RichTextEditor (existing path).
                        'imported' → read-only iframe viewer of the
                                     full HTML document. The operator
                                     edits via the Re-import button in
                                     the card's extra slot, not in-place
                                     (architect decision §12.4: no
                                     inline editing of imported bodies).
                    */}
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) =>
                        prev.body_source !== curr.body_source ||
                        prev.body_html   !== curr.body_html
                      }
                    >
                      {() => {
                        const isImported = form.getFieldValue('body_source') === 'imported'
                        const bodyHtml   = form.getFieldValue('body_html') || ''
                        if (isImported) {
                          return (
                            <div style={{ flex: 1, minHeight: 320, padding: 12 }}>
                              <iframe
                                title='Imported HTML body'
                                srcDoc={bodyHtml || '<p style="color:#bfbfbf;font-family:sans-serif;padding:24px">(empty imported body)</p>'}
                                sandbox='allow-same-origin'
                                style={{
                                  width:        '100%',
                                  minHeight:    320,
                                  border:       '1px solid #f0f0f0',
                                  borderRadius: 4,
                                  background:   '#ffffff',
                                  display:      'block'
                                }}
                              />
                            </div>
                          )
                        }
                        return (
                          <div style={{ flex: 1, minHeight: 320 }}>
                            {editorEngine === 'ckeditor' ? (
                              <React.Suspense
                                fallback={
                                  <div style={{ padding: 24, textAlign: 'center' }}>
                                    <Spin />
                                  </div>
                                }
                              >
                                <CKEditorRichText
                                  ref={editorRef}
                                  value={bodyHtml}
                                  onChange={handleBodyChange}
                                  placeholder='Compose your message…'
                                  uploadContext={{
                                    campaignId: persistedId,
                                    token: getAccessToken(store.getState().auth),
                                    tableName: TABLE_FILE_URLS_EMAIL_CAMPAIGNS,
                                    idField: FIELD_EMAIL_CAMPAIGN_ID
                                  }}
                                />
                              </React.Suspense>
                            ) : (
                              <RichTextEditor
                                value={bodyHtml}
                                onChange={handleBodyChange}
                                placeholder='Compose your message…'
                              />
                            )}
                          </div>
                        )
                      }}
                    </Form.Item>
                    {(campaignAttachments.length > 0 || accountFileCount > 0) && (
                      <div
                        style={{
                          padding: '6px 12px',
                          borderTop: '1px solid #f0f0f0',
                          background: '#fafafa'
                        }}
                      >
                        <Space size={6} wrap>
                          <PaperClipOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                          <Text type='secondary' style={{ fontSize: 12 }}>Attached:</Text>
                          {campaignAttachments.map((a) => (
                            <Tag
                              key={a.id}
                              style={{ cursor: 'pointer', margin: 0 }}
                              onClick={() => setAttachmentsOpen(true)}
                            >
                              {a.file_name}
                            </Tag>
                          ))}
                          {accountFileCount > 0 && (
                            <Tooltip title='Files that belong to one account each. Each account receives only its own; see the Recipients pane.'>
                              <Tag color='blue' style={{ margin: 0 }}>
                                Per-account files: {accountFileCount} for {accountsWithFiles} account
                                {accountsWithFiles === 1 ? '' : 's'}
                              </Tag>
                            </Tooltip>
                          )}
                        </Space>
                      </div>
                    )}
                  </Card>
                </Col>

                {/* ── RIGHT: recipients + preview ─────────────────────── */}
                <Col
                  xs={24}
                  md={12}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 'calc(100vh - 180px)'
                  }}
                >
                  <Card
                    size='small'
                    title='Recipients'
                    style={{
                      marginBottom: 12,
                      flex: '0 0 380px',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}
                    bodyStyle={{ flex: 1, overflow: 'auto', padding: 8 }}
                  >
                    {isEditing ? (
                      // Form.Item shouldUpdate keeps the recipients pane in
                      // sync with the live send_mode value — without it the
                      // pane gets the snapshot from initial render and the
                      // team-mode rendering wouldn't react to a mode toggle.
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) =>
                          prev.send_mode !== curr.send_mode ||
                          prev.subject !== curr.subject ||
                          prev.body_html !== curr.body_html
                        }
                      >
                        {() => (
                          <RecipientsTab
                            campaignId={persistedId}
                            defaultBodyHtml={form.getFieldValue('body_html') || ''}
                            defaultSubject={form.getFieldValue('subject') || ''}
                            sendMode={
                              form.getFieldValue('send_mode') === 'team' ? 'team' : 'individual'
                            }
                            onSelectionChange={setPreviewSelection}
                            selectedRecipientId={
                              previewSelection?.kind === 'individual'
                                ? previewSelection.recipient.email_recipients__id
                                : null
                            }
                            selectedAccountId={
                              previewSelection?.kind === 'team' ? previewSelection.accountId : null
                            }
                            onStaged={setCampaignRecipientCount}
                            accountFiles={accountFiles}
                            noticeEmail={
                              sourceLink
                                ? { locked: isLockedForEditing, onRefresh: handleRefreshFromSource, source: sourceLink.kind }
                                : null
                            }
                          />
                        )}
                      </Form.Item>
                    ) : (
                      <Alert
                        type='info'
                        showIcon
                        message='Save the draft first to add recipients.'
                      />
                    )}
                  </Card>

                  <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 360 }}>
                    {/* Preview re-renders when subject/body/body_source change so
                        the substituted output tracks the user's edits live.
                        body_source flips the PreviewPane between thin-shell-
                        wrapped (jodit) and verbatim (imported) iframe render. */}
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) =>
                        prev.subject     !== curr.subject     ||
                        prev.body_html   !== curr.body_html   ||
                        prev.body_source !== curr.body_source
                      }
                    >
                      {() => (
                        <PreviewPane
                          selection={previewSelection}
                          campaignSubject={form.getFieldValue('subject') || ''}
                          campaignBodyHtml={form.getFieldValue('body_html') || ''}
                          bodySource={
                            form.getFieldValue('body_source') === 'imported'
                              ? 'imported'
                              : form.getFieldValue('body_source') === 'ckeditor'
                              ? 'ckeditor'
                              : 'jodit'
                          }
                          campaignCc={ccRecipients.map((c) => c.email_address)}
                          campaignBcc={bccRecipients.map((c) => c.email_address)}
                        />
                      )}
                    </Form.Item>
                  </div>
                </Col>
              </Row>

              {/* PR #57: Body-card "Import HTML" modal — paste or
                  upload a complete HTML document (Stripo export etc.).
                  On submit, flip body_source to 'imported' and replace
                  body_html with the imported document. The next save
                  call persists with the controller-side sanitize gate
                  bypassed for the imported branch. */}
              <ImportHtmlModal
                open={importHtmlOpen}
                initialHtml={
                  form.getFieldValue('body_source') === 'imported'
                    ? (form.getFieldValue('body_html') || '')
                    : ''
                }
                onCancel={() => setImportHtmlOpen(false)}
                onSubmit={(html) => {
                  form.setFieldsValue({
                    body_html:   html,
                    body_source: 'imported'
                  })
                  setImportHtmlOpen(false)
                }}
              />

              {/* Phase 4b: Body-card "Merge fields" modal — chip strip
                  that was previously always-on. Click a chip to copy the
                  placeholder; paste into the subject or body. */}
              <Modal
                title='Merge fields'
                open={mergeFieldsOpen}
                onCancel={() => setMergeFieldsOpen(false)}
                footer={null}
                width={520}
                destroyOnClose
              >
                <p style={{ marginTop: 0, color: '#8c8c8c' }}>
                  Click a placeholder to copy. Paste it into the subject or body where you want
                  the dynamic value to appear.
                </p>
                <Space wrap size={[6, 8]}>
                  {MERGE_FIELDS.map((f) => {
                    const isHovered = hoveredMergeField === f.placeholder
                    return (
                      <Tooltip title={f.tooltip} key={f.placeholder}>
                        <Tag
                          onMouseEnter={() => setHoveredMergeField(f.placeholder)}
                          onMouseLeave={() =>
                            setHoveredMergeField((curr) =>
                              curr === f.placeholder ? null : curr
                            )
                          }
                          style={{
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            userSelect: 'none',
                            borderColor: isHovered ? '#1677ff' : undefined,
                            background: isHovered ? '#e6f4ff' : undefined,
                            margin: 0
                          }}
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(f.placeholder)
                              toast.success(`Copied ${f.placeholder}`)
                              // One copy per session — close the modal so the
                              // user can paste straight away.
                              setMergeFieldsOpen(false)
                            } catch {
                              showErrorToast('Clipboard copy failed — select and copy manually.')
                            }
                          }}
                        >
                          {f.placeholder}
                          <CopyOutlined
                            style={{
                              marginLeft: 6,
                              fontSize: 11,
                              opacity: isHovered ? 1 : 0,
                              transition: 'opacity 120ms ease',
                              color: '#1677ff'
                            }}
                          />
                        </Tag>
                      </Tooltip>
                    )
                  })}
                </Space>
              </Modal>

              {/* Track D1: Test Send modal. Cfg row points at a test
                  account (e.g. Acequia Capital); the modal shows that
                  account's contacts as checkboxes (is_self pre-checked)
                  plus an ad-hoc free-text input for addresses not wired
                  to the test account. Contact-linked sends use real
                  merge_data; ad-hoc sends get visible placeholder
                  synthesis in the edge fn. Backend RPC re-validates
                  draft, delegation, sending-domain, email syntax, and
                  per-message cap; errors surface verbatim. */}
              <Modal
                title='Test send'
                open={testSendOpen}
                onCancel={() => {
                  if (testSendSending) return
                  setTestSendOpen(false)
                }}
                maskClosable={!testSendSending}
                closable={!testSendSending}
                width={560}
                destroyOnClose
                okText={`Send test (${testSendRecipientsPayload.length})`}
                cancelText='Cancel'
                okButtonProps={{
                  loading: testSendSending,
                  disabled:
                    testSendRecipientsPayload.length === 0 ||
                    (testSendNeedsAccount && !testSendAsAccountId),
                  icon: <ExperimentOutlined />
                }}
                cancelButtonProps={{ disabled: testSendSending }}
                onOk={handleTestSend}
              >
                <Alert
                  type='info'
                  showIcon
                  style={{ marginBottom: 12 }}
                  message='Recipients see a [TEST]-prefixed copy of this campaign.'
                  description='The draft is not queued or modified. Contact-linked recipients render with real merge fields; ad-hoc addresses show visible placeholders (e.g. [FirstName]) so you can see which fields would substitute in production.'
                />
                {testSendNeedsAccount && (
                  <Form.Item
                    label='Use the attachments of'
                    required
                    tooltip='This campaign carries per-account attachments. The test is sent as the account you choose: with its merge values and its own files, to the test recipients below - never to the account itself.'
                    style={{ marginBottom: 12 }}
                  >
                    <Select
                      showSearch
                      placeholder='Choose a staged account'
                      options={testSendAccountOptions}
                      optionFilterProp='label'
                      value={testSendAsAccountId}
                      onChange={setTestSendAsAccountId}
                      disabled={testSendSending}
                    />
                  </Form.Item>
                )}
                {testSendLastErrors.length > 0 && (
                  <Alert
                    type='warning'
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${testSendLastErrors.length} recipient${testSendLastErrors.length === 1 ? '' : 's'} failed on the last attempt`}
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {testSendLastErrors.map((e, i) => (
                          <li key={`${e.email}-${i}`}>
                            <Text code>{e.email}</Text>
                            <Text type='secondary' style={{ marginLeft: 6 }}>
                              {e.message}
                            </Text>
                          </li>
                        ))}
                      </ul>
                    }
                  />
                )}
                {testSendAccount ? (
                  <>
                    <div style={{ marginBottom: 6 }}>
                      <Text strong>{testSendAccount.label}</Text>
                      {testSendContacts.length > 0 && (
                        <Text type='secondary' style={{ marginLeft: 8, fontSize: 12 }}>
                          {testSendCheckedContactIds.size} of {testSendContacts.length} selected
                        </Text>
                      )}
                    </div>
                    {testSendContacts.length === 0 ? (
                      <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
                        No contacts with usable emails on this account. Use the free-text field
                        below.
                      </Text>
                    ) : (
                      <div style={{ marginBottom: 12, maxHeight: 220, overflow: 'auto' }}>
                        <Space direction='vertical' size={4} style={{ width: '100%' }}>
                          {testSendContacts.map((c) => {
                            const fullName = `${c.first_name} ${c.last_name}`.trim() || c.email
                            return (
                              <Checkbox
                                key={c.contact_id}
                                checked={testSendCheckedContactIds.has(c.contact_id)}
                                disabled={testSendSending}
                                onChange={(e) => {
                                  const next = new Set(testSendCheckedContactIds)
                                  if (e.target.checked) next.add(c.contact_id)
                                  else next.delete(c.contact_id)
                                  setTestSendCheckedContactIds(next)
                                }}
                              >
                                <Text>{fullName}</Text>
                                <Text type='secondary' style={{ marginLeft: 6, fontSize: 12 }}>
                                  &lt;{c.email}&gt;
                                </Text>
                                {c.is_self && (
                                  <Tag color='blue' style={{ marginLeft: 6 }}>
                                    you
                                  </Tag>
                                )}
                              </Checkbox>
                            )
                          })}
                        </Space>
                      </div>
                    )}
                  </>
                ) : (
                  <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
                    No test account configured. Set{' '}
                    <Text code>cfg.app_config('email','test_recipients')</Text> to point at an
                    account, or use the free-text field below.
                  </Text>
                )}
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  Other recipients
                </Text>
                <Input.TextArea
                  rows={2}
                  placeholder='alice@example.com, bob@example.com'
                  value={testSendAdHocText}
                  disabled={testSendSending}
                  onChange={(e) => setTestSendAdHocText(e.target.value)}
                />
                <Text type='secondary' style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  Comma, semicolon, or whitespace separated. Duplicates and addresses already
                  ticked above are ignored.
                </Text>
              </Modal>

              {/* Phase 4b: Body-card "Attachments" modal — drop-in
                  FileUploadManager. On close we refetch the campaign
                  detail so the inline chip strip + badge count pick up
                  any add / remove operations the modal performed. */}
              <Modal
                title='Attachments'
                open={attachmentsOpen}
                onCancel={() => {
                  setAttachmentsOpen(false)
                  refreshCampaignStatus()
                }}
                footer={null}
                width={720}
                destroyOnClose
              >
                <Alert
                  type='info'
                  showIcon
                  style={{ marginBottom: 12 }}
                  message='Attachments are stored per campaign and shared across all recipients.'
                />
                {isEditing && persistedId && isLockedForEditing ? (
                  // Past draft the attachments are what was (or is being)
                  // sent: shown, not editable.
                  <ReadOnlyFileList
                    files={campaignAttachments.map((a) => ({
                      id: a.id,
                      name: a.file_name,
                      path: a.file_url
                    }))}
                    emptyText='This campaign has no shared attachments.'
                  />
                ) : isEditing && persistedId ? (
                  <FileUploadManager
                    files={[]}
                    tableName={TABLE_FILE_URLS_EMAIL_CAMPAIGNS}
                    idField={FIELD_EMAIL_CAMPAIGN_ID}
                    idValue={persistedId}
                  />
                ) : (
                  <Alert
                    type='warning'
                    showIcon
                    message='Save the draft first to add attachments.'
                  />
                )}
              </Modal>
            </Form>
          </div>
        </Spin>
      )}
    </div>
  )
}
